import React from "react";
import AttachFile from "@material-ui/icons/AttachFile";
import { useTranslation } from "react-i18next";
import Box from "Components/Atoms/Box";
import Typography from "Components/Atoms/Typography";
import useRestaurant from "CustomHooks/useRestaurant";
import type { CustomDataValue } from "types/shared/helper/customElements";
import {
    EMPTY_CUSTOM_DATA_CATALOG,
    presentCustomData
} from "utils/customDataPresentation";
import { toCurrencyString } from "utils/helper";

export function stringifyCustomDataValue(
    value: CustomDataValue,
    key?: string
): React.ReactNode {
    if (key === "balance")
        return toCurrencyString(value as number, "€", false, 1);
    if (typeof value === "string") {
        let url: URL;
        try {
            url = new URL(value);
        } catch {
            return value;
        }
        if (url.protocol !== "http:" && url.protocol !== "https:") return value;
        return (
            <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    maxWidth: "100%",
                    color: "inherit"
                }}
            >
                <AttachFile
                    aria-hidden="true"
                    style={{ fontSize: "1em", flexShrink: 0 }}
                />
                <span>Anhang öffnen</span>
            </a>
        );
    }
    if (typeof value === "number") return value;
    if (typeof value === "boolean") return value ? "Ja" : "Nein";
    if (value && typeof value === "object") {
        return Object.entries(value)
            .map(([name, answer]) => `${name}: ${String(answer ?? "")}`)
            .join(", ");
    }
    return "";
}

export default function CustomDataFields({
    entries,
    compact = false,
    className
}: {
    entries: [string, CustomDataValue][];
    compact?: boolean;
    className?: string;
}) {
    const { customDataCatalog = EMPTY_CUSTOM_DATA_CATALOG } = useRestaurant();
    const { i18n } = useTranslation();
    return (
        <>
            {entries.map(([key, value]) => {
                const display = presentCustomData(
                    customDataCatalog,
                    key,
                    value,
                    i18n?.language || "de"
                );
                if (
                    display.value === 0 ||
                    display.value === "0" ||
                    display.value === ""
                )
                    return null;
                const rendered = stringifyCustomDataValue(display.value, key);
                if (
                    compact &&
                    (rendered === "" ||
                        rendered === null ||
                        rendered === undefined)
                )
                    return null;
                return (
                    <Box
                        key={key}
                        className={
                            compact
                                ? undefined
                                : "space-between reservation-field"
                        }
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 8
                        }}
                    >
                        <Typography
                            variant={compact ? "text-4" : "text-3"}
                            block
                            weight={compact ? "bold" : undefined}
                            className={className}
                            style={{
                                flex: "1 1 50%",
                                minWidth: 0,
                                overflowWrap: "anywhere"
                            }}
                        >
                            {display.label}
                        </Typography>
                        <Typography
                            variant={compact ? "text-4" : "text-2"}
                            block
                            color={compact ? undefined : "subdued"}
                            className={className}
                            style={{
                                flex: "1 1 50%",
                                minWidth: 0,
                                overflowWrap: "anywhere",
                                textAlign: "right"
                            }}
                        >
                            {rendered}
                        </Typography>
                    </Box>
                );
            })}
        </>
    );
}
